import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box, Typography, IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  ChangeEvent, FC, useEffect, useState,
} from 'react';

import CustomInput from 'components/CustomInput';
import { CONDITION_ITEMS } from 'containers/Inventory/constants';
import { useInventoryContext } from 'context/InventoryContext';
import { useInventoryFilter } from 'context/InventoryFilterContext';

import { InventoryConditionItemType } from 'constants/enums';
import { InventoryConditionItemOption } from 'types/customInventoryFilters.interface';

import { StyledCustomIconButton, StyledFilterContainer, StyledOptionsBox } from './index.styled';

const SimpleInventoryFilter: FC = () => {
  const {
    workingSimpleFilters,
    setWorkingSimpleFilters,
  } = useInventoryFilter();

  const {
    getOptionsForItem,
  } = useInventoryContext();

  const [state, setState] = useState<{
    expandedFilters: Record<string, boolean>;
    highlightedFields: Record<string, boolean>;
  }>({
    expandedFilters: {},
    highlightedFields: {},
  });

  useEffect(() => {
    const initialHighlights: Record<string, boolean> = {};
    const initialExpanded: Record<string, boolean> = {};

    Object.entries(workingSimpleFilters).forEach(([key, filter]) => {
      if (filter.value) {
        initialHighlights[key] = true;
        initialExpanded[key] = true;
      }
    });

    setState({
      expandedFilters: initialExpanded,
      highlightedFields: initialHighlights,
    });
  }, [workingSimpleFilters]);

  const toggleFilter = (filterValue: string) => {
    setState((prev) => ({
      ...prev,
      expandedFilters: {
        ...prev.expandedFilters,
        [filterValue]: !prev.expandedFilters[filterValue],
      },
    }));
  };

  const handleInputChange = (filterKey: string, value: string) => {
    const filter = {
      field: filterKey,
      operator: 'CONTAINS',
      value,
    };
    setWorkingSimpleFilters({
      ...workingSimpleFilters,
      [filterKey]: filter,
    });
  };

  const removeFilter = (filterKey: string) => {
    const updatedFilters = { ...workingSimpleFilters };
    delete updatedFilters[filterKey];
    setWorkingSimpleFilters(updatedFilters);
  };

  const addCheckOptionToFilters = (filterKey: string, value: string) => {
    if (workingSimpleFilters[filterKey]) {
      const filterValues = workingSimpleFilters[filterKey].value as string[];
      filterValues.push(value);
      const newFilter = { ...workingSimpleFilters[filterKey], value: filterValues };
      setWorkingSimpleFilters({
        ...workingSimpleFilters,
        [filterKey]: newFilter,
      });
    } else {
      const filter = {
        field: filterKey,
        operator: 'EQ',
        value: [value],
      };
      setWorkingSimpleFilters({
        ...workingSimpleFilters,
        [filterKey]: filter,
      });
    }
  };

  const removeCheckOptionFromFilters = (filterKey: string, value: string) => {
    if (workingSimpleFilters[filterKey]) {
      const filterValues = workingSimpleFilters[filterKey].value as string[];
      const updatedValues = filterValues.filter((v) => v !== value);

      if (updatedValues.length === 0) {
        const updatedFilters = { ...workingSimpleFilters };
        delete updatedFilters[filterKey];
        setWorkingSimpleFilters(updatedFilters);
      } else {
        const newFilter = { ...workingSimpleFilters[filterKey], value: updatedValues };
        setWorkingSimpleFilters({
          ...workingSimpleFilters,
          [filterKey]: newFilter,
        });
      }
    }
  };

  const handleCheckChange = (checked:any, filterKey: string, value: string) => {
    if (checked) {
      addCheckOptionToFilters(filterKey, value);
    } else {
      removeCheckOptionFromFilters(filterKey, value);
    }
  };

  const isChecked = (filterKey: string, value: string): boolean => {
    if (workingSimpleFilters[filterKey]) {
      const valuesArray = workingSimpleFilters[filterKey].value as string[];
      if (valuesArray.includes(value)) {
        return true;
      }
    }
    return false;
  };

  const handleSwitchChange = (checked:any, filterKey: string) => {
    if (!checked) {
      const updatedFilters = { ...workingSimpleFilters };
      delete updatedFilters[filterKey];
      setWorkingSimpleFilters(updatedFilters);
    } else {
      const filter = {
        field: filterKey,
        operator: 'EQ',
        value: 'true',
      };
      setWorkingSimpleFilters({
        ...workingSimpleFilters,
        [filterKey]: filter,
      });
    }
  };

  const isSwitched = (filterKey: string) => workingSimpleFilters[filterKey]?.value === 'true';

  const showFilterContent = (value: string, key: string) => (
    state.expandedFilters[value] ?? Boolean(workingSimpleFilters[key])
  );

  const handleRemoveFilter = (filter: any) => {
    removeFilter(filter.key);
    setState((prev) => ({
      ...prev,
      highlightedFields: {
        ...prev.highlightedFields,
        [filter.key]: false,
      },
    }));
  };

  const handleFilterChanges = (
    { target: { value } }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    filter: InventoryConditionItemOption,
  ) => {
    handleInputChange(filter.key, value);

    if (!value) {
      setState((prev) => ({
        ...prev,
        highlightedFields: {
          ...prev.highlightedFields,
          [filter.key]: false,
        },
      }));
    }
  };

  const handleOnBlur = (filter: InventoryConditionItemOption) => {
    if (workingSimpleFilters[filter.key]?.value) {
      setState((prev) => ({
        ...prev,
        highlightedFields: {
          ...prev.highlightedFields,
          [filter.key]: true,
        },
      }));
    }
  };

  const handleOnFocus = (filter: InventoryConditionItemOption) => {
    if (workingSimpleFilters[filter.key]?.value) {
      setState((prev) => ({
        ...prev,
        highlightedFields: {
          ...prev.highlightedFields,
          [filter.key]: false,
        },
      }));
    }
  };

  const getFilterStructure = (filter: InventoryConditionItemOption) => {
    switch (filter.type) {
      case InventoryConditionItemType.STRING:
        return (
          <Box>
            {showFilterContent(filter.value, filter.key) && (
              <CustomInput
                placeholder="Contains..."
                fullWidth
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {workingSimpleFilters[filter.key]?.value && (
                        <StyledCustomIconButton
                          size="small"
                          onClick={handleRemoveFilter}
                          highlighted={state.highlightedFields[filter.key]}
                        >
                          <CloseIcon fontSize="small" />
                        </StyledCustomIconButton>
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
                size="small"
                sx={{
                  paddingRight: '8px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: state.highlightedFields[filter.key] ? 'highlightColor.main' : 'common.white',
                    transition: 'background-color 0.3s ease',
                  },
                }}
                value={workingSimpleFilters[filter.key]?.value || ''}
                onChange={(e) => handleFilterChanges(e, filter)}
                onBlur={() => handleOnBlur(filter)}
                onFocus={() => handleOnFocus(filter)}
              />
            )}
          </Box>
        );

      case InventoryConditionItemType.OPTIONS:
        return (
          <Box maxWidth={400} display="flex" flexWrap="wrap" justifyContent="space-between" gap={1}>
            {showFilterContent(filter.value, filter.key) && (
              getOptionsForItem(filter.key).map((option) => {
                const checked: boolean = isChecked(filter.key, option.key as string);
                return (
                  <StyledOptionsBox key={option.key} checked={checked}>
                    <FormControlLabel
                      sx={{ margin: 0 }}
                      control={(
                        <Checkbox
                          checked={checked}
                          onChange={(e) => handleCheckChange(e.target.checked, filter.key, option.key as string)}
                        />
                    )}
                      label={option.value}
                    />
                  </StyledOptionsBox>
                );
              })
            )}
          </Box>
        );
      case InventoryConditionItemType.BOOLEAN:
        return (
          <Box display="flex" alignItems="center" justifyContent="flex-start" gap={2}>
            <Typography fontWeight={700}>
              {filter.value}
            </Typography>
            <Switch
              checked={isSwitched(filter.key)}
              onChange={(e) => handleSwitchChange(e.target.checked, filter.key)}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      {CONDITION_ITEMS.map((filter) => {
        const isExpanded = showFilterContent(filter.value, filter.key);

        return (
          <StyledFilterContainer key={filter.value} expanded={isExpanded}>
            {filter.type !== InventoryConditionItemType.BOOLEAN && (
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={isExpanded ? 1 : 0}>
              <Typography fontWeight={700}>
                {filter.value}
              </Typography>
              <IconButton onClick={() => toggleFilter(filter.value)} size="small">
                {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </Box>
            )}

            {getFilterStructure(filter)}
          </StyledFilterContainer>
        );
      })}
    </Box>
  );
};

export default SimpleInventoryFilter;
